import { useEffect, useMemo, useState } from "react";
import { getCapitalGains } from "./StockTradeService";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const fmt = (value) => toNumber(value).toFixed(2);

export default function StockCapitalGains() {
  const [data, setData] = useState({ financialYears: [], dataIntegrityIssues: [] });
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const result = await getCapitalGains();
    setData(result || { financialYears: [], dataIntegrityIssues: [] });
  };

  const sortedYears = useMemo(() => {
    const years = Array.isArray(data.financialYears) ? data.financialYears : [];
    return [...years].sort((a, b) => (b.financialYear || "").localeCompare(a.financialYear || ""));
  }, [data]);

  const toggleYear = (fy) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fy)) next.delete(fy);
      else next.add(fy);
      return next;
    });
  };

  const issues = Array.isArray(data.dataIntegrityIssues) ? data.dataIntegrityIssues : [];

  return (
    <div className="stocks-section">
      {issues.length > 0 && (
        <div className="card stocks-tax-warning">
          <div className="card-title">Data Integrity Warnings</div>
          <ul>
            {issues.map((issue, idx) => (
              <li key={idx}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {sortedYears.length === 0 && issues.length === 0 && (
        <div className="card">
          <div className="stocks-empty">No realized capital gains yet. Closed trades will appear here grouped by financial year.</div>
        </div>
      )}

      {sortedYears.map((fy) => {
        const isOpen = expanded.has(fy.financialYear);
        const entries = Array.isArray(fy.entries) ? fy.entries : [];
        return (
          <div className="card" key={fy.financialYear}>
            <div className="stocks-tax-header" onClick={() => toggleYear(fy.financialYear)}>
              <div className="card-title">{fy.financialYear}</div>
              <div className="stocks-tax-summary">
                <span className={`stocks-badge ${toNumber(fy.stcgTotal) >= 0 ? "stocks-good" : "stocks-bad"}`}>
                  STCG {fmt(fy.stcgTotal)} ({fy.stcgGainCount})
                </span>
                <span className={`stocks-badge ${toNumber(fy.ltcgTotal) >= 0 ? "stocks-good" : "stocks-bad"}`}>
                  LTCG {fmt(fy.ltcgTotal)} ({fy.ltcgGainCount})
                </span>
                <span className="stocks-tax-toggle">{isOpen ? "▲" : "▼"}</span>
              </div>
            </div>
            {isOpen && (
              <div className="data-table-wrap">
                <table className="stocks-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Buy Date</th>
                      <th>Sell Date</th>
                      <th>Qty</th>
                      <th>Buy Value</th>
                      <th>Sell Value</th>
                      <th>Holding Days</th>
                      <th>Gain/Loss</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, idx) => (
                      <tr key={idx}>
                        <td>{entry.symbol}</td>
                        <td>{entry.buyDate || "--"}</td>
                        <td>{entry.sellDate || "--"}</td>
                        <td>{fmt(entry.quantity)}</td>
                        <td>{fmt(entry.buyValue)}</td>
                        <td>{fmt(entry.sellValue)}</td>
                        <td>{entry.holdingDays ?? "--"}</td>
                        <td className={toNumber(entry.realizedPnl) >= 0 ? "stocks-good" : "stocks-bad"}>
                          {fmt(entry.realizedPnl)}
                        </td>
                        <td>
                          <span className={`stocks-pill ${entry.gainType === "LTCG" ? "stocks-pill-open" : "stocks-pill-partial"}`}>
                            {entry.gainType}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan="9">No entries for this financial year.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
